// -- [Public marketing current-data API client]

const SECONDS_PER_91_25_DAYS = 7884000

export interface PagesByTypeItem {
  total: number
  last3Months: number
}

export interface EstimatedTotalTimeSavedSeconds {
  totalTimeSavedSeconds: number
  totalTimeSavedSecondsLast3Months: number
}

export interface FileAccessTimeSavedItem {
  count: number
  estimatedTotalTimeSavedSeconds: number
  avgEstimatedManualSeconds: number
}

export interface IncomingEmailFilingItem {
  incomingEmailCount: number
  documentsCreatedTotal: number
  filesScannedCount: number
  estimatedFilingTimeSeconds: number
  estimatedScanTimeSeconds: number
  estimatedTotalTimeSavedSeconds: number
}

export interface BulkUploadFilingItem {
  bulkUploadCount: number
  documentsCreatedTotal: number
  filesScannedCount: number
  estimatedFilingTimeSeconds: number
  estimatedScanTimeSeconds: number
  estimatedTotalTimeSavedSeconds: number
}

export interface LuceneSearchStatsItem {
  count: number
  processingTimeTotal: number
}

export interface PublicPricingPlanInfo {
  billingPlanKey: string
  label?: string
  description?: string
  pricePerUserPerMonthAud?: number | null
  minUsers?: number | null
  maxUsers?: number | null
  minPricePerMonthAud?: number | null
  maxPricePerMonthAud?: number | null
  maxDocumentsPerMonth?: number | null
  documentsUnlimited?: boolean
  notes?: string
  isYearlyPlan?: boolean
  basePlanKey?: string
  annualDiscountPercentage?: number
}

export interface PublicPricingInfo {
  currency: string
  currentPerUserPerMonthAud?: number
  currentPerUserPerMonthFormatted?: string
  plans: Record<string, PublicPricingPlanInfo>
  pricingHistory?: unknown[]
}

export interface CurrentData {
  totalPagesStored: number
  totalPagesStoredLast3Months: number
  pagesByType: {
    service: PagesByTypeItem
    sales: PagesByTypeItem
    finance: PagesByTypeItem
  }
  activeDealershipsCount: number
  platformTransactionsTotalAud: number
  platformTransactionsTotalLast3MonthsAud: number
  estimatedTotalTimeSavedSeconds?: EstimatedTotalTimeSavedSeconds
  fileAccessTimeSaved?: FileAccessTimeSavedItem
  incomingEmailFiling?: IncomingEmailFilingItem
  bulkUploadFiling?: BulkUploadFilingItem
  luceneSearchStats?: LuceneSearchStatsItem
  luceneSearchStatsLast3Months?: LuceneSearchStatsItem
  threeMonthsSeconds?: number
  pricingInfo?: PublicPricingInfo
}

export interface CurrentDataEnvelope {
  data: CurrentData | []
  cachedAt: number | null
  serverTime: number
  _messages?: unknown[]
}

export function isCurrentData(data: CurrentData | []): data is CurrentData {
  return Array.isArray(data) ? false : typeof (data as CurrentData).totalPagesStored === 'number'
}

// -- [Default backup when APIs are unavailable (snapshot from api.drivible.com)]
export const FALLBACK_CURRENT_DATA: CurrentData = {
  totalPagesStored: 4868490,
  totalPagesStoredLast3Months: 743624,
  pagesByType: {
    service: { total: 3060399, last3Months: 495151 },
    sales: { total: 1610509, last3Months: 211608 },
    finance: { total: 197632, last3Months: 36913 },
  },
  activeDealershipsCount: 19,
  platformTransactionsTotalAud: 2956651.99,
  platformTransactionsTotalLast3MonthsAud: 314174.42,
  estimatedTotalTimeSavedSeconds: {
    totalTimeSavedSeconds: 115133228,
    totalTimeSavedSecondsLast3Months: 25375116,
  },
  fileAccessTimeSaved: {
    count: 141500,
    estimatedTotalTimeSavedSeconds: 76425353,
    avgEstimatedManualSeconds: 542.79,
  },
  incomingEmailFiling: {
    incomingEmailCount: 342021,
    documentsCreatedTotal: 611292,
    filesScannedCount: 67982,
    estimatedFilingTimeSeconds: 35921370,
    estimatedScanTimeSeconds: 6840420,
    estimatedTotalTimeSavedSeconds: 29080950,
  },
  bulkUploadFiling: {
    bulkUploadCount: 17895,
    documentsCreatedTotal: 180415,
    filesScannedCount: 161597,
    estimatedFilingTimeSeconds: 10342725,
    estimatedScanTimeSeconds: 715800,
    estimatedTotalTimeSavedSeconds: 9626925,
  },
  luceneSearchStats: {
    count: 187172,
    processingTimeTotal: 96966.611047,
  },
  luceneSearchStatsLast3Months: {
    count: 35938,
    processingTimeTotal: 8374.382559,
  },
  threeMonthsSeconds: 7884000,
}

export const FALLBACK_CACHED_AT = 1786077839
export const FALLBACK_SERVER_TIME = 1786080407

// -- [Prefer configured/beta host; fall back to production when that host is down]
export const PRODUCTION_MARKETING_API_BASE = 'https://api.drivible.com'

// -- [Env: VITE_PUBLIC_MARKETING_API_URL — full URL or base; can include /v1/public-marketing/current-data]
export function getPublicMarketingApiBase(): string | null {
  const value = import.meta.env.VITE_PUBLIC_MARKETING_API_URL
  if (!value?.trim()) return null
  const trimmed = value.trim().replace(/\/$/, '')
  if (trimmed.includes('/v1/public-marketing/current-data')) {
    return trimmed.replace(/\/v1\/public-marketing\/current-data$/, '')
  }
  return trimmed
}

export function getCurrentDataEndpoint(): string | null {
  const base = getPublicMarketingApiBase()
  if (!base) return null
  return base + '/v1/public-marketing/current-data'
}

export function getPublicPricingEndpoint(): string | null {
  const base = getPublicMarketingApiBase()
  if (!base) return null
  return base + '/v1/public-marketing/pricing'
}

function getProductionCurrentDataEndpoint(): string {
  return PRODUCTION_MARKETING_API_BASE + '/v1/public-marketing/current-data'
}

function getProductionPricingEndpoint(): string {
  return PRODUCTION_MARKETING_API_BASE + '/v1/public-marketing/pricing'
}

function isProductionMarketingUrl(url: string): boolean {
  try {
    return new URL(url).hostname === 'api.drivible.com'
  } catch {
    return false
  }
}

function withQuery(url: string, query: URLSearchParams): string {
  return query.size > 0 ? `${url}?${query.toString()}` : url
}

export interface FetchCurrentDataResult {
  ok: true
  data: CurrentData
  cachedAt: number
  serverTime: number
}

export interface FetchCurrentDataError {
  ok: false
  error: 'no_config' | 'empty_data' | 'network' | 'invalid_response'
}

export type FetchCurrentDataOutcome = FetchCurrentDataResult | FetchCurrentDataError

export interface FetchCurrentDataOptions {
  includePricingInfo?: boolean
  expandPricingInfo?: boolean
}

export interface FetchPublicPricingResult {
  ok: true
  data: PublicPricingInfo
}

export interface FetchPublicPricingError {
  ok: false
  error: 'no_config' | 'network' | 'invalid_response'
}

export type FetchPublicPricingOutcome = FetchPublicPricingResult | FetchPublicPricingError

async function fetchCurrentDataFromUrl(
  url: string,
  options: FetchCurrentDataOptions = {}
): Promise<FetchCurrentDataOutcome> {
  try {
    const query = new URLSearchParams()
    if (options.includePricingInfo === true) query.set('includePricingInfo', 'true')
    if (options.expandPricingInfo === true) query.set('expandPricingInfo', 'true')
    const res = await fetch(withQuery(url, query), { method: 'GET' })
    if (!res.ok) return { ok: false, error: 'network' }

    const raw = (await res.json()) as unknown
    if (!raw || typeof raw !== 'object') return { ok: false, error: 'invalid_response' }

    const obj = raw as Record<string, unknown>
    let data: CurrentData | []
    let cachedAt: number
    let serverTime: number

    if (isCurrentData(obj as unknown as CurrentData)) {
      // -- [Direct response: payload at top level (e.g. some proxies)]
      data = obj as unknown as CurrentData
      const now = Math.floor(Date.now() / 1000)
      cachedAt = (obj.cachedAt as number) ?? now
      serverTime = (obj.serverTime as number) ?? now
    } else if (
      typeof obj.data !== 'undefined' &&
      (obj.cachedAt == null || typeof obj.cachedAt === 'number') &&
      typeof obj.serverTime === 'number'
    ) {
      // -- [Envelope: { data, cachedAt, serverTime }]
      data = obj.data as CurrentData | []
      cachedAt = (obj.cachedAt as number) ?? Math.floor(Date.now() / 1000)
      serverTime = obj.serverTime as number
    } else {
      return { ok: false, error: 'invalid_response' }
    }

    if (!isCurrentData(data)) return { ok: false, error: 'empty_data' }

    return { ok: true, data, cachedAt, serverTime }
  } catch {
    return { ok: false, error: 'network' }
  }
}

export async function fetchCurrentData(
  options: FetchCurrentDataOptions = {}
): Promise<FetchCurrentDataOutcome> {
  const url = getCurrentDataEndpoint()
  if (!url) return { ok: false, error: 'no_config' }

  const primary = await fetchCurrentDataFromUrl(url, options)
  if (primary.ok) return primary

  // -- [Beta/staging down: try production so stats stay live]
  if (!isProductionMarketingUrl(url)) {
    const fallback = await fetchCurrentDataFromUrl(getProductionCurrentDataEndpoint(), options)
    if (fallback.ok) return fallback
  }

  return primary
}

function isPublicPricingInfo(raw: unknown): raw is PublicPricingInfo {
  if (!raw || typeof raw !== 'object') return false
  const obj = raw as Record<string, unknown>
  if (typeof obj.currency !== 'string') return false
  if (!obj.plans || typeof obj.plans !== 'object') return false
  return true
}

async function fetchPublicPricingInfoFromUrl(
  url: string,
  options: { expandPricingInfo?: boolean } = {}
): Promise<FetchPublicPricingOutcome> {
  try {
    const query = new URLSearchParams()
    if (options.expandPricingInfo === true) query.set('expandPricingInfo', 'true')
    const res = await fetch(withQuery(url, query), { method: 'GET' })
    if (!res.ok) return { ok: false, error: 'network' }
    const raw = (await res.json()) as unknown
    if (!isPublicPricingInfo(raw)) return { ok: false, error: 'invalid_response' }
    return { ok: true, data: raw }
  } catch {
    return { ok: false, error: 'network' }
  }
}

export async function fetchPublicPricingInfo(
  options: { expandPricingInfo?: boolean } = {}
): Promise<FetchPublicPricingOutcome> {
  const url = getPublicPricingEndpoint()
  if (!url) return { ok: false, error: 'no_config' }

  const primary = await fetchPublicPricingInfoFromUrl(url, options)
  if (primary.ok) return primary

  if (!isProductionMarketingUrl(url)) {
    const fallback = await fetchPublicPricingInfoFromUrl(getProductionPricingEndpoint(), options)
    if (fallback.ok) return fallback
  }

  return primary
}

export { SECONDS_PER_91_25_DAYS }
